import React, { useEffect, useState } from 'react';
import { Card, Typography, Table, message, Spin, Tabs, Form, Input, Alert } from 'antd';
import { useAuth } from '../../../components/common/security/AuthContext';
import { changeMyPassword, fetchSqlData } from '../../../services/api';
import { useAssignedTasks } from '../../../components/common/TaskManagement/useAssignedTasks';
import { useNavigate } from 'react-router-dom';
import { useGetPersonnel } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title } = Typography;
const { TabPane } = Tabs;

interface User {
  id: number;
  username: string;
  email?: string;
  full_name?: string;
  role?: string;
}

const UserProfile: React.FC = () => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userResState, setUserResState] = useState<any>(null);
  const {
    tasks: activeTasks,
    isLoading: activeTasksLoading,
    navigateToTask,
  } = useAssignedTasks({ statuses: ['pending', 'in progress', 'blocked'] });
  const {
    tasks: completedTasks,
    isLoading: completedTasksLoading,
    navigateToTask: navigateToCompletedTask,
  } = useAssignedTasks({ statuses: ['completed'] });
  const navigate = useNavigate();
  const [pwForm] = Form.useForm();
  const [pwLoading, setPwLoading] = useState(false);
  const { data: personnelResp } = useGetPersonnel();

  useEffect(() => {
    const fetchProfileAndTasks = async () => {
      setLoading(true);
      try {
        // Profil bilgisi
        const userRes = await fetchSqlData('it_users', 'username', user);
        console.log('user:', user, 'userRes:', userRes);
        setUserResState(userRes);
        const arr = userRes && userRes.data ? userRes.data : (Array.isArray(userRes) ? userRes : []);
        if (arr.length > 0) {
          const base = arr[0] as any;
          setProfile(base);
          // If full_name or email are missing on it_users, try resolving from personnel view
          const pid = base?.personnel_id;
          if (pid) {
            try {
              const pArr = Array.isArray(personnelResp) ? personnelResp : (personnelResp as any)?.data || [];
              const p = (pArr || []).find((row: any) => String(row?.pers_id) === String(pid));
              if (p) {
                const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim();
                const email = p.email || undefined;
                setProfile((prev) => ({ ...(prev || base), full_name: fullName || base?.full_name, email: email || base?.email }));
              }
            } catch {}
          }
        }
      } catch (e) {
        message.error('Profile or tasks could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchProfileAndTasks();
  }, [user]);

  if (loading) return <Spin style={{ margin: 40 }} />;

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0, color: '#1677ff' }}>User Profile</Title>
      }
      bordered={false}
      style={{ maxWidth: 1200, margin: '0 auto', marginTop: 32 }}
    >
      <Tabs defaultActiveKey="1">
        <TabPane tab="Profile Information" key="1">
          {profile ? (
            <div style={{ marginBottom: 24 }}>
              <b>Username:</b> {profile.username} <br />
              <b>Full Name:</b> {profile.full_name || '-'} <br />
              <b>Email:</b> {profile.email || '-'} <br />
              <b>Role:</b> {profile.role || '-'}
            </div>
          ) : (
            <div style={{ color: 'red', marginBottom: 24 }}>
              Profile information could not be loaded.<br/>
              <span style={{fontSize:12, color:'#888'}}>Debug: {JSON.stringify(user)}<br/>{userResState ? JSON.stringify(userResState) : ''}</span>
            </div>
          )}
        </TabPane>
        <TabPane tab="Assigned Tasks" key="2">
          <Table
            loading={activeTasksLoading}
            columns={[
              { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
              { title: 'Description', dataIndex: 'description', key: 'description' },
              { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
              { title: 'Action', key: 'action', width: 140,
                render: (_: any, record: any) => (
                  <Button type="link" onClick={() => navigateToTask(navigate, record)}>
                    Open
                  </Button>
                ),
              },
            ]}
            dataSource={activeTasks}
            rowKey="id"
            pagination={{ pageSize: 10, size: 'small' }}
            size="small"
            locale={{ emptyText: 'No tasks assigned.' }}
          />
        </TabPane>
        <TabPane tab="Completed Tasks" key="4">
          <Table
            loading={completedTasksLoading}
            columns={[
              { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
              { title: 'Description', dataIndex: 'description', key: 'description' },
              { title: 'Status', dataIndex: 'status', key: 'status', width: 110 },
              { title: 'Action', key: 'action', width: 140,
                render: (_: any, record: any) => (
                  <Button type="link" onClick={() => navigateToCompletedTask(navigate, record)}>
                    Open
                  </Button>
                ),
              },
            ]}
            dataSource={completedTasks}
            rowKey="id"
            pagination={{ pageSize: 10, size: 'small' }}
            size="small"
            locale={{ emptyText: 'No completed tasks.' }}
          />
        </TabPane>
        <TabPane tab="Şifre Değiştir" key="3">
          <div style={{ maxWidth: 520 }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Şifre gereksinimleri"
              description="En az 8 karakter, en az 1 büyük harf, 1 küçük harf, 1 rakam ve 1 özel karakter içermelidir."
            />
            <Form
              form={pwForm}
              layout="vertical"
              onFinish={async (values) => {
                try {
                  setPwLoading(true);
                  const res = await changeMyPassword({
                    old_password: values.old_password,
                    new_password: values.new_password,
                  });
                  if (res?.error) {
                    message.error(res.error);
                    return;
                  }
                  message.success(res?.message || 'Şifre başarıyla değiştirildi. Lütfen tekrar giriş yapın.');
                  pwForm.resetFields();
                  logout();
                } catch (e: any) {
                  message.error(e?.response?.data?.error || 'Şifre değiştirilemedi.');
                } finally {
                  setPwLoading(false);
                }
              }}
            >
              <Form.Item
                name="old_password"
                label="Mevcut şifre"
                rules={[{ required: true, message: 'Mevcut şifrenizi giriniz' }]}
              >
                <Input.Password autoComplete="current-password" />
              </Form.Item>

              <Form.Item
                name="new_password"
                label="Yeni şifre"
                rules={[
                  { required: true, message: 'Yeni şifrenizi giriniz' },
                  { min: 8, message: 'En az 8 karakter olmalıdır' },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                name="confirm_new_password"
                label="Yeni şifre (tekrar)"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Yeni şifrenizi tekrar giriniz' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const np = getFieldValue('new_password');
                      if (!value || value === np) return Promise.resolve();
                      return Promise.reject(new Error('Şifreler eşleşmiyor'));
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Button action="edit" type="primary" htmlType="submit" loading={pwLoading}>
                Şifreyi değiştir
              </Button>
            </Form>
          </div>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default UserProfile; 